// src/shared/ui/Button.jsx
//
// No shared button existed anywhere in the app — every module wrote its own
// `<button className="...">` (golive's `gl-btn`, codebase's `.btn`, IT's
// `.it-btn`, plain Tailwind ad hoc elsewhere). This is the first shared
// primitive built directly on the design tokens in index.css (brand-*,
// radius-card) rather than a hardcoded hex/px value.
//
// Usage:
//   <Button onClick={submit}>Save</Button>
//   <Button variant="secondary" size="sm">Cancel</Button>
//   <Button variant="danger" loading={isDeleting}>Delete</Button>

import { forwardRef } from "react";
import PropTypes from "prop-types";
import { Loader2 } from "lucide-react";

const VARIANT_CLASSES = {
  primary: "bg-brand-600 text-white hover:bg-brand-700 disabled:bg-brand-600/60",
  secondary: "bg-white text-ink border border-line hover:bg-gray-50 disabled:opacity-60",
  ghost: "bg-transparent text-ink hover:bg-gray-100 disabled:opacity-60",
  danger: "bg-red-600 text-white hover:bg-red-700 disabled:bg-red-600/60",
};

const SIZE_CLASSES = {
  sm: "px-3 py-1.5 text-xs gap-1.5",
  md: "px-4 py-2 text-sm gap-2",
  lg: "px-5 py-2.5 text-sm gap-2",
};

const Button = forwardRef(function Button(
  { variant = "primary", size = "md", loading = false, disabled = false, className = "", children, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || loading}
      className={[
        "inline-flex items-center justify-center rounded-card font-semibold transition-colors whitespace-nowrap",
        VARIANT_CLASSES[variant] ?? VARIANT_CLASSES.primary,
        SIZE_CLASSES[size] ?? SIZE_CLASSES.md,
        className,
      ].filter(Boolean).join(" ")}
      {...props}
    >
      {loading && <Loader2 size={14} className="animate-spin" />}
      {children}
    </button>
  );
});

Button.propTypes = {
  variant: PropTypes.oneOf(["primary", "secondary", "ghost", "danger"]),
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Button;
